import { useNavigate } from "react-router-dom";
import { FaFileAlt, FaPenNib, FaMagic, FaDownload, FaBuilding, FaTrophy, FaHeart } from "react-icons/fa";


const Desktop23 = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-[#f9f9f9] w-full overflow-x-hidden">
      {/* ================= HERO SECTION ================= */}
      <section className="relative bg-gradient-to-b from-[#06010A] to-[#260345] text-white flex flex-col md:flex-row items-center justify-between px-8 md:px-16 lg:px-24 py-20 overflow-hidden">
        {/* Decorative Ellipses */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-[400px] h-[400px] bg-[#7B3FE4]/40 rounded-full blur-3xl top-[-100px] left-[-100px]" />
          <div className="absolute w-[350px] h-[350px] bg-[#E6C1FF]/30 rounded-full blur-3xl bottom-[-150px] right-[-100px]" />
        </div>

        {/* Left Content */}
        <div className="relative z-10 max-w-xl">
          <h3 className="text-lg text-[#ffffffbf] mb-3">AI Cover Letter Generator</h3>
          <h1 className="text-4xl md:text-5xl font-semibold leading-tight mb-6">
            Craft a personalized cover letter as easily as introducing yourself
          </h1>
          <p className="text-gray-200 mb-8 text-base md:text-lg">
            Let us help you translate your resume and unique skills into a persuasive cover letter,
            personally tailored to highlight your suitability for the role.
          </p>

          {/* ✅ Get Started Button */}
          <button
            onClick={() => navigate("/auth")}
            className="bg-[#ffc403] text-black font-bold px-6 py-3 rounded-xl hover:bg-[#e6b800] transition"
          >
            Get Started
          </button>
        </div>

        {/* Right Image */}
        <div className="relative z-10 mt-12 md:mt-0">
          <img
            src="https://c.animaapp.com/mge1sasxzrNszH/img/image-60.png"
            alt="Resume Preview"
            className="w-[350px] md:w-[450px] object-contain"
          />
        </div>
      </section>

      {/* ================= CUSTOMIZE SECTION ================= */}
      <section className="relative bg-[#f9f9f9] text-[#22033c] py-24 px-6 md:px-16 text-center overflow-hidden">
        {/* Subtle Background Shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-[400px] h-[400px] bg-[#E7E4E9]/30 rounded-full blur-3xl top-[-150px] left-[-150px]" />
          <div className="absolute w-[300px] h-[300px] bg-[#E7E4E9]/20 rounded-full blur-3xl bottom-[-100px] right-[-100px]" />
        </div>

        {/* Headings */}
        <div className="relative z-10 mb-16">
          <h2 className="text-3xl md:text-4xl font-semibold mb-3">
            Customize quickly and confidently
          </h2>
          <p className="text-gray-700 max-w-2xl mx-auto text-lg">
            Emphasize your key strengths and align your tone with every job description.
          </p>
        </div>

        {/* Features */}
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-12 max-w-6xl mx-auto">
          {/* 1️⃣ Tailored Content */}
          <div className="flex flex-col items-center text-center space-y-3">
            <FaFileAlt className="w-10 h-10 text-[#22033c]" />
            <h3 className="text-xl font-semibold">Tailored Content</h3>
            <p className="text-gray-700 text-base max-w-xs">
              Generate letters personalized to your role while highlighting your top achievements and skills.
            </p>
          </div>

          {/* 2️⃣ Professional Tone */}
          <div className="flex flex-col items-center text-center space-y-3">
            <FaPenNib className="w-10 h-10 text-[#22033c]" />
            <h3 className="text-xl font-semibold">Professional Tone</h3>
            <p className="text-gray-700 text-base max-w-xs">
              Produce polished, confident writing suitable for your target industry.
            </p>
          </div>

          {/* 3️⃣ Interactive Editing */}
          <div className="flex flex-col items-center text-center space-y-3">
            <FaMagic className="w-10 h-10 text-[#22033c]" />
            <h3 className="text-xl font-semibold">Interactive Editing</h3>
            <p className="text-gray-700 text-base max-w-xs">
              Get smart suggestions to improve clarity, adjust tone, or refine phrasing for different applications.
            </p>
          </div>
        </div>

        {/* Seamlessly Integrated */}
        <div className="relative z-10 mt-16 flex flex-col items-center text-center space-y-3">
          <FaDownload className="w-10 h-10 text-[#22033c]" />
          <h3 className="text-xl font-semibold">Seamlessly Integrated</h3>
          <p className="text-gray-700 text-base max-w-md">
            Export your letter in multiple formats or attach it directly to job applications.
          </p>
        </div>
      </section>

      {/* ================= HIGHLIGHT SECTION ================= */}
      <section className="relative bg-[#f9f9f9] text-[#22033c] py-24 px-6 md:px-16 text-center overflow-hidden">
        {/* Subtle Background Shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-[600px] h-[600px] bg-[#E7E4E9]/40 rounded-full blur-3xl top-[-150px] left-[-150px]" />
          <div className="absolute w-[500px] h-[500px] bg-[#E7E4E9]/30 rounded-full blur-3xl bottom-[-100px] right-[-150px]" />
        </div>

        {/* Headings */}
        <div className="relative z-10 mb-16">
          <h2 className="text-3xl md:text-4xl font-semibold mb-3">
            Highlight what matters most
          </h2>
          <p className="text-gray-700 max-w-2xl mx-auto text-lg">
            Emphasize the qualities that make you the perfect fit for any role.
          </p>
        </div>

        {/* Features */}
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-12 max-w-6xl mx-auto">
          {/* 1️⃣ Core Skills */}
          <div className="flex flex-col items-center text-center space-y-3">
            <FaBuilding className="w-10 h-10 text-[#22033c]" />
            <h3 className="text-xl font-semibold">Core Skills</h3>
            <p className="text-gray-700 text-base max-w-xs">
              Showcase your expertise and relevance to the position.
            </p>
            <p className="text-gray-500 italic text-sm max-w-xs">
              Example: “Experienced in developing production-ready models and scaling data pipelines.”
            </p>
          </div>

          {/* 2️⃣ Company Value Alignment */}
          <div className="flex flex-col items-center text-center space-y-3">
            <FaTrophy className="w-10 h-10 text-[#22033c]" />
            <h3 className="text-xl font-semibold">Company Value Alignment</h3>
            <p className="text-gray-700 text-base max-w-xs">
              Highlight measurable results and milestones.
            </p>
            <p className="text-gray-500 italic text-sm max-w-xs">
              Example: “Increased model accuracy by 15% at Company X.”
            </p>
          </div>

          {/* 3️⃣ Key Achievements */}
          <div className="flex flex-col items-center text-center space-y-3">
            <FaHeart className="w-10 h-10 text-[#22033c]" />
            <h3 className="text-xl font-semibold">Key Achievements</h3>
            <p className="text-gray-700 text-base max-w-xs">
              Demonstrate alignment with company goals and mission.
            </p>
            <p className="text-gray-500 italic text-sm max-w-xs">
              Example: “I share your company’s data-driven mission to power informed decision-making.”
            </p>
          </div>
        </div>

        {/* Explore Templates Button */}
        <div className="relative z-10 mt-16">
          <button className="text-[#22033c] font-semibold text-lg hover:underline">
            Explore all Templates →
          </button>
        </div>
      </section>
    </div>
  );
};

export default Desktop23;
